import { useEffect, useCallback, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';

const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

interface SessionTimeoutOptions {
  onTimeout?: () => void;
  isAuthenticated: boolean;
}

export const useSessionTimeout = ({ onTimeout, isAuthenticated }: SessionTimeoutOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      onTimeout?.();
    } catch (error) {}
  }, [onTimeout]);

  const resetTimeout = useCallback(() => {
    if (!isAuthenticated) return;

    clearTimers();
    lastActivityRef.current = Date.now();

    // Set direct timeout for logout
    timeoutRef.current = setTimeout(handleLogout, INACTIVITY_TIMEOUT);
  }, [isAuthenticated, clearTimers, handleLogout]);

  const handleActivity = useCallback(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    // Only reset if significant time has passed to avoid excessive resets
    if (timeSinceLastActivity > 30000) { // 30 seconds
      resetTimeout();
    }
  }, [resetTimeout]);

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      return;
    }

    // Initialize timeout
    resetTimeout();

    // Activity event listeners
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Handle visibility change (tab focus)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleActivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimers();
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, resetTimeout, handleActivity, clearTimers]);

};
